import { UserDto } from '../security/model/userDto';
import { User } from '../security/model/user';
import { UserPreferenceHeader } from '../security/model/userPreferenceHeader';
import * as CryptoJS from 'crypto-js';
import swal from 'sweetalert2';

export class SecurityHelper {

  static WebStorage = class {

    static Keys = class {
      static LOGGED_IN_USER_DATA = 'ivi-data-1';
      static ACTIVE_ORDER_ID = 'ivi-data-2';
      static TRUSTED_GUID = 'ivi-data-3';
      static AUTHENTICATED_WS = 'ivi-data-4';
      static SHIPMANAGER_LOGO_WS = 'ivi-data-5';
      static LIGHT_VERSION = 'ivi-data-6';
      static WORK_OFFLINE = 'ivi-data-7';
      static SHIP_CONTACT_WS = 'ivi-data-8';

      static IS_UNAUTHORIZED = 'ivi-check-1'; // Do not encrypt flag for unauthorized access
      static KY_WEB = 'ivi-check-2'; // Do not encrypt key for web storage
      static KY_RES = 'ivi-check-3';
    }

    static SetItem(key: string, value: string) {

      // localStorage = Shared between all tabs and windows from the same origin.
      // risk: The data does not expire. It remains after the browser restart / refresh and even OS reboot.
      // workaround: Added code in AppComponent that clears localStorage when closing the browser / on page refresh.
      localStorage.setItem(key, value);

      // sessionStorage = Data exists only within the current browser tab
      // Data retained on page refresh
      sessionStorage.setItem(key, value);

      // Use localStorage & sessionStorage
      // to keep data available between different tabs of the same browser even after page refresh BUT not after closing the browser
    }

    static GetItem(key: string): string {

      let storedData: any = null;

      let sessionData = sessionStorage.getItem(key);
      let localData = localStorage.getItem(key);

      if (sessionData && localData) {
        // use any of the 2;
        storedData = sessionData;
      }

      // On page refresh, localStorage gets cleared. Retrieve data from sessionStorage, then restore data in localStorage.
      else if (sessionData && !localData) {
        localStorage.setItem(key, sessionData);
        storedData = sessionData;
      }

      // On new tab/browser, sessionStorage gets cleared. Retrieve data from localStorage, then restore data in sessionStorage.
      else if (!sessionData && localData) {
        sessionStorage.setItem(key, localData);
        storedData = localData;
      }

      // Not found
      else if (!sessionData && !localData) {
        storedData = null;
      }

      return storedData;
    }

    static RemoveItem(key: string) {

      localStorage.removeItem(key);

      sessionStorage.removeItem(key);

    }

  }

  // Store ShipContact Info
  static SetShipContact(contact: any) {

    this.WebStorage.SetItem(this.WebStorage.Keys.SHIP_CONTACT_WS, JSON.stringify(contact))

  }

  // Get Logged-in UserDto
  static GetShipContact(): any {
    let contact: any;

    let storedData = this.WebStorage.GetItem(this.WebStorage.Keys.SHIP_CONTACT_WS);

    if (storedData) {
      contact = JSON.parse(storedData)
    }
    else {
      contact = null;
    }

    return contact;
  }

  // Store Logged-in UserDto
  static SetLoggedInUserDto(userDto: UserDto) {

    this.SetHTTPRequestsUnathorized(false);

    // store decrypted user data
    this.WebStorage.SetItem(this.WebStorage.Keys.KY_WEB, userDto.user[0].xidWebK);
    this.WebStorage.SetItem(this.WebStorage.Keys.KY_RES, userDto.user[0].xidResK);

    // store dto
    this.WebStorage.SetItem(this.WebStorage.Keys.LOGGED_IN_USER_DATA, JSON.stringify(userDto))

  }

  // Get Logged-in UserDto
  static GetLoggedInUserDto(): UserDto {
    let userDto: any = new UserDto();

    let storedData = this.WebStorage.GetItem(this.WebStorage.Keys.LOGGED_IN_USER_DATA);

    if (storedData) {
      userDto = JSON.parse(storedData)
    }
    else {
      userDto = null;
    }

    return userDto;;
  }

  // Get Logged-in User Data
  static GetLoggedInUserData(): User {

    let user: User = new User();

    let userDto: UserDto = this.GetLoggedInUserDto();

    if (userDto) {
      user = userDto.user[0];
    }

    return user;
  }

  // Set Access Token for Change Password API
  static SetLoggedInUserDataExpiredPassword(user: any) {

    let dummyUserDto: UserDto = new UserDto();
    dummyUserDto.user = [];

    var dummyUser = new User();
    dummyUser.userID = user.userID;
    dummyUser.isPasswordExpired = true;

    dummyUserDto.user.push(dummyUser);
    this.WebStorage.SetItem(this.WebStorage.Keys.LOGGED_IN_USER_DATA, JSON.stringify(dummyUserDto))
  }

  // Set Unathorized
  static SetHTTPRequestsUnathorized(isUnathorized: boolean) {

    this.WebStorage.SetItem(this.WebStorage.Keys.IS_UNAUTHORIZED, JSON.stringify(isUnathorized))
  }

  // Get Unathorized
  static IsHTTPRequestsUnathorized(showErrorMessage: boolean): boolean {
    let isUnathorized: boolean = false;

    let storedData = this.WebStorage.GetItem(this.WebStorage.Keys.IS_UNAUTHORIZED);

    if (storedData != null) {
      isUnathorized = JSON.parse(storedData)

      if (isUnathorized && showErrorMessage) {
        swal.fire({
          title: "Unauthorized access",
          html: '<span>Please login again to resolve the issue.</span>',
          icon: "error",
          showCloseButton: true,
          allowOutsideClick: false,
          allowEscapeKey: false
        }).then(() => {
          SecurityHelper.GoBackToLoginPage();
        });
      }

    }

    return isUnathorized;
  }

  // Go back to login page
  static GoBackToLoginPage() {

    localStorage.removeItem(SecurityHelper.WebStorage.Keys.AUTHENTICATED_WS);

    sessionStorage.clear();

    window.location.assign('../');
  }

  // Get Logged-in User Preference List
  static GetLoggedInUserPreferenceList(): UserPreferenceHeader[] {

    let userPreferenceList: UserPreferenceHeader[] = [];

    let userDto: UserDto = this.GetLoggedInUserDto();
    if (userDto) {

      userPreferenceList = userDto.userPreference;
    }

    return userPreferenceList;
  }

  // Store Light Version
  static SetLightVersion(lightVersion: boolean) {
    this.WebStorage.SetItem(this.WebStorage.Keys.LIGHT_VERSION, JSON.stringify(lightVersion))
  }

  // Get Light Version
  static GetLightVersion(): boolean {
    let lightVersion: boolean = false;

    let storedData = this.WebStorage.GetItem(this.WebStorage.Keys.LIGHT_VERSION);

    if (storedData) {
      lightVersion = JSON.parse(storedData)
    }
    else {
      lightVersion = false;
    }

    return lightVersion;
  }

  // Store Work Offline
  static SetWorkOffline(workOffline: boolean) {
    this.WebStorage.SetItem(this.WebStorage.Keys.WORK_OFFLINE, JSON.stringify(workOffline))
  }

  // Get Light Version
  static GetWorkOffline(): boolean {
    let workOffline: boolean = false;

    let storedData = this.WebStorage.GetItem(this.WebStorage.Keys.WORK_OFFLINE);

    if (storedData) {
      workOffline = JSON.parse(storedData)
    }
    else {
      workOffline = false;
    }

    return workOffline;
  }

  // Store Active OrderID
  static SetActiveOrderID(OrderID: number) {

    // Save in session storage only
    // Data exists only within the current browser tab
    // FYI: If user opened a new tab (while Order page is open in another tab), user can still navigate to other pages as long as it is not same tab as where the Order page is currently open.
    sessionStorage.setItem(this.WebStorage.Keys.ACTIVE_ORDER_ID, OrderID.toString());
  }

  // Remove Active OrderID
  static RemoveActiveOrderID() {

    this.WebStorage.RemoveItem(this.WebStorage.Keys.ACTIVE_ORDER_ID);

  }

  // Get Active OrderID
  static GetActiveOrderID(): number {

    let orderID: any = null;

    // Retrieve data from session storage
    // FYI: If user opened a new tab (while Order page is open in another tab), user can still navigate to other pages as long as it is not same tab as where the Order page is currently open.
    let storedData = sessionStorage.getItem(this.WebStorage.Keys.ACTIVE_ORDER_ID);

    if (storedData) {
      let numStoredData = Number(storedData);

      if (isNaN(numStoredData) || (!isNaN(numStoredData) && numStoredData <= 0)) {
        orderID = null;
      }
      else {
        orderID = numStoredData;
      }

    }
    else {
      orderID = null;
    }

    return orderID;
  }

  static decryptAPIResponseAuthorized(res: any): any {

    SecurityHelper.decryptAPIResponse(res, SecurityHelper.WebStorage.GetItem(SecurityHelper.WebStorage.Keys.KY_RES));
  }

  private static decryptAPIResponse(res: any, key: string): any {
    try {

      const keyBytes = CryptoJS.enc.Hex.parse(key);
      const encryptedBytes = CryptoJS.enc.Base64.parse(res.entity);

      // Extract IV from the beginning of the encrypted data
      const ivBytes = CryptoJS.lib.WordArray.create(encryptedBytes.words.slice(0, 4), 16);
      const cipherBytes = CryptoJS.lib.WordArray.create(encryptedBytes.words.slice(4));

      const decrypted = CryptoJS.AES.decrypt(
        { ciphertext: cipherBytes } as CryptoJS.lib.CipherParams,
        keyBytes,
        { iv: ivBytes, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
      );

      res.entity = JSON.parse(decrypted.toString(CryptoJS.enc.Utf8));

    } catch (e) {

      console.warn('Unable to decrypt', res);
    }
  }

  // Store TrustedGUID
  static SetTrustedGUID(GUIDSTR: string) {

    // Save in session storage only
    // Data exists only within the current browser tab
    // FYI: If user opened a new tab (while Order page is open in another tab), user can still navigate to other pages as long as it is not same tab as where the Order page is currently open.
    sessionStorage.setItem(this.WebStorage.Keys.TRUSTED_GUID, GUIDSTR);
  }

  static SetTrustedGUIDDto(GUIDSTR: string) {

    //this.WebStorage.SetItem(this.WebStorage.Keys.TRUSTED_GUID, JSON.stringify(GUIDSTR + '-' + Date.now()))
    this.WebStorage.SetItem(this.WebStorage.Keys.TRUSTED_GUID, JSON.stringify(GUIDSTR))

  }

  // Remove TrustedGUID
  static RemoveTrustedGUID() {

    this.WebStorage.RemoveItem(this.WebStorage.Keys.TRUSTED_GUID);

  }
  static isTrustedGUIDExist(): boolean {
    let allow: boolean = false;

    let storedData = this.WebStorage.GetItem(this.WebStorage.Keys.TRUSTED_GUID);

    if (storedData) {
      allow = true;
    }
    else {
      allow = false;
    }

    return allow;
  }

  static SetAutheticatedWS() {
    this.WebStorage.SetItem(this.WebStorage.Keys.AUTHENTICATED_WS, 'true');
  }
  static SetShipManagerLogo(logo: any) {
    this.WebStorage.SetItem(this.WebStorage.Keys.SHIPMANAGER_LOGO_WS, logo);
  }
  static SetUnAutheticatedWS() {
    this.WebStorage.SetItem(this.WebStorage.Keys.AUTHENTICATED_WS, 'false');
  }
  static isAutheticatedWS(): boolean {
    let authenticated: boolean = false;

    let storedData = this.WebStorage.GetItem(this.WebStorage.Keys.AUTHENTICATED_WS);

    if (storedData == 'true') {
      authenticated = true;
    }
    else {
      authenticated = false;
    }

    return authenticated;
  }

  static trimByChar(string: any, character: any) {
    const first = [...string].findIndex(char => char !== character);
    const last = [...string].reverse().findIndex(char => char !== character);
    return string.substring(first, string.length - last);
  }

  static getTrustedCode(): any {
    let storedData = this.WebStorage.GetItem(this.WebStorage.Keys.TRUSTED_GUID);

    return this.trimByChar(storedData, '"'); //storedData.replace('"','');
  }

  static GetShipManagerLogo(): any {
    let storedData = this.WebStorage.GetItem(this.WebStorage.Keys.SHIPMANAGER_LOGO_WS);

    return storedData;
  }

  static CreateUUID() {
    //return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    //  var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    //  return v.toString(16);
    //});
    return crypto.getRandomValues(new Uint32Array(4)).join('-');
  }

  static PreferenceIDs = class {
    static TIMEZONEOFFSET = 0;
    static TIMEZONE = 1;
    static CULTURE = 2;
    static EMAIL_FETCHING = 3;
    static EMAIL_ADDRESS = 4;
    static EMAIL_PASSWORD = 5;
    static EMAIL_USERNAME = 6;
    static EMAIL_DISPLAY_NAME = 7;
    static SHIPID = 8;
  }


  static GetUserPreference(userDto: UserDto, PreferenceID: number): string {

    let preferenceValue: any = null;

    let userPreference = userDto.userPreference;
    if (userPreference != null) {
      let listCount = userPreference.length;
      for (let i = 0; i < listCount; i++) {
        if (userPreference[i].preferenceID == PreferenceID) {
          preferenceValue = userPreference[i].preferenceValue;
          break;
        }
      }
    }

    return preferenceValue;
  }

  static isLeapYear(year: any) {
    if ((year & 3) != 0) return false;
    return ((year % 100) != 0 || (year % 400) == 0);
  };

  static getDayOftheYear(start: any): number {
    var da: any = new Date(start);
    var dayCount = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
    var yr = da.getYear();
    var mn = da.getMonth();
    var dn = da.getDate();
    var dayOfYear = dayCount[mn] + dn;
    if (mn > 1 && this.isLeapYear(yr)) dayOfYear++;

    return dayOfYear;
  }

  static getMonthYear(day: any): string {

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"
    ];

    let dateNow: any = new Date();
    let firstDayOfYear = new Date(dateNow.getFullYear(), 0, 1);
    firstDayOfYear.setDate(firstDayOfYear.getDate() + (Math.round(day) - 1));
    // console.log('getMonthYear', firstDayOfYear, day);
    var yr = firstDayOfYear.getFullYear();
    var mn = firstDayOfYear.getMonth() + 1;
    var dn = firstDayOfYear.getDate();
    let monmthYear = monthNames[mn - 1].toString() + ' ' + yr.toString();

    return monmthYear;
  }

  static ModuleAccessCodes = class {

    static Maintenance = class {
      // Crew Pool
      static CrewPoolView = 18181;
      static CrewPoolAdd = 18182;
      static CrewPoolEdit = 18183;
      static CrewPoolDelete = 18184;     
    }

  }

  static Constants = class {
    static SUCCESSFUL_LOGIN: Boolean = false;
  }
}
